(() => {
  if (window.__hoyostarGridV6) { window.__hoyostarGridV6.scan(document); return; }
  const instances = new Map();
  class HoyostarHoverGrid {
    constructor(container) {
      this.container = container;
      this.controller = new AbortController();
      this.timers = new Set();
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.listen = (target, name, handler, options = {}) => target.addEventListener(name, handler, { ...options, signal: this.controller.signal });
      this.later = (fn, ms) => {
        const timer = setTimeout(() => { this.timers.delete(timer); fn(); }, ms);
        this.timers.add(timer);
        return timer;
      };
      this.cards = this.container.querySelectorAll('.hoyostar-hover-grid__card');
      this.hoverEffectCards = this.container.querySelectorAll('.has-hover-effect');
      this.desktop = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
      this.canAnimate = this.desktop.matches && !this.reducedMotion.matches;
      this.init();
      this.listen(document, 'visibilitychange', () => { if (document.hidden) this.stop(); });
      this.listen(window, 'pagehide', () => this.stop());
      const mediaState = () => `${this.mobile.matches}/${this.desktop.matches}/${this.reducedMotion.matches}`;
      this.mediaState = mediaState();
      const refresh = () => {
        if (this.mediaState === mediaState()) return;
        this.destroy();
        mount(this.container);
      };
      this.listen(this.reducedMotion, 'change', refresh);
      this.listen(this.desktop, 'change', refresh);
      this.listen(this.mobile, 'change', refresh);
    }

    stop() {
      this.cards.forEach(card => { if (card.stopAnimation) card.stopAnimation(); });
      this.cursorStops?.forEach(stop => stop());
    }

    destroy() {
      this.stop();
      this.controller.abort();
      this.observer?.disconnect();
      this.timers.forEach(timer => clearTimeout(timer));
      this.timers.clear();
      this.cards.forEach(card => {
        delete card.startAnimation;
        delete card.stopAnimation;
        card.style.removeProperty('--hyo-frame-fade');
      });
      this.container.classList.remove('is-enhanced');
      this.container.querySelectorAll('img[data-hyo-hydrated]').forEach(img => img.remove());
      instances.delete(this.container);
    }

    init() {
      if (this.canAnimate) this.hoverEffectCards.forEach(card => this.setupCard(card));
      this.setupEntranceAnimation();

      if (this.canAnimate) {
        this.cards.forEach(card => this.setupCursorEffect(card));
      }
    }

    setupCursorEffect(card) {
      const tracker = card.querySelector('.hoyostar-cursor-tracker');
      if (!tracker) return;

      let mouseX = 0, mouseY = 0;
      let currentX = 0, currentY = 0;
      let isHovering = false;
      let rafId = null;

      const DAMPING = 0.12;
      const ICON_SIZE = 56;

      const lerp = (start, end, factor) => start + (end - start) * factor;

      const updateLoop = () => {
        if (!isHovering || this.reducedMotion.matches) { rafId = null; return; }
        currentX = lerp(currentX, mouseX, DAMPING);
        currentY = lerp(currentY, mouseY, DAMPING);
        tracker.style.transform = `translate3d(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px, 0)`;
        if (Math.abs(currentX - mouseX) + Math.abs(currentY - mouseY) > 0.1) {
          rafId = requestAnimationFrame(updateLoop);
        } else {
          rafId = null;
        }
      };

      const onMouseEnter = (e) => {
        isHovering = true;
        const rect = card.getBoundingClientRect();
        mouseX = e.clientX - rect.left - (ICON_SIZE / 2);
        mouseY = e.clientY - rect.top - (ICON_SIZE / 2);
        currentX = mouseX;
        currentY = mouseY;
        updateLoop();
      };

      const onMouseMove = (e) => {
        const rect = card.getBoundingClientRect();
        mouseX = e.clientX - rect.left - (ICON_SIZE / 2);
        mouseY = e.clientY - rect.top - (ICON_SIZE / 2);

        const maxX = rect.width - ICON_SIZE;
        const maxY = rect.height - ICON_SIZE;
        mouseX = Math.max(0, Math.min(mouseX, maxX));
        mouseY = Math.max(0, Math.min(mouseY, maxY));
        if (isHovering && rafId === null) updateLoop();
      };

      const onMouseLeave = () => {
        isHovering = false;
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
      };
      (this.cursorStops ||= []).push(onMouseLeave);

      this.listen(card, 'mouseenter', onMouseEnter);
      this.listen(card, 'mousemove', onMouseMove);
      this.listen(card, 'mouseleave', onMouseLeave);
    }

    setupEntranceAnimation() {
      if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
        this.cards.forEach(card => card.classList.add('is-visible'));
        return;
      }
      this.container.classList.add('is-enhanced');
      const home = this.container.dataset.hyoEntrance === 'settle';
      const grid = this.container.querySelector('.hoyostar-hover-grid');
      const observer = new IntersectionObserver(entries => {
        const visible = entries.filter(entry => entry.isIntersecting && !entry.target.classList.contains('is-visible'));
        if (!visible.length) return;
        const columns = home ? 1 : getComputedStyle(grid).gridTemplateColumns.split(' ').length;
        if (home) {
          for (let i = visible.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [visible[i], visible[j]] = [visible[j], visible[i]];
          }
        }
        visible.forEach((entry, rank) => {
          const card = entry.target;
          observer.unobserve(card);
          if (card.classList.contains('is-visible')) return;
          const index = Array.prototype.indexOf.call(this.cards, card);
          const delay = home ? Math.min(rank * (this.mobile.matches ? 70 : 90), this.mobile.matches ? 140 : 450) : (index % columns) * 70;
          this.later(() => card.classList.add('is-visible'), delay);
        });
      }, {threshold: 0.08, rootMargin: '0px 0px -20px 0px'});
      // A focused link must never stay visually hidden while waiting to enter.
      this.listen(this.container, 'focusin', event => {
        const card = event.target.closest('.hoyostar-hover-grid__card');
        if (card) { card.classList.add('is-visible'); observer.unobserve(card); }
      });

      this.observer = observer;
      this.cards.forEach(card => {
        if (!card.classList.contains('is-visible')) observer.observe(card);
      });
    }

    setupCard(card) {
      const cover = card.querySelector('.cover-image');
      if (!cover) return;
      const interval = Math.max(120, Number(card.dataset.frameInterval) || 200);
      const templates = [...card.querySelectorAll('template[data-hyo-frame]')];
      const fadeDuration = Math.min(160, interval * 0.6);
      let frames = [], wanted = false, playing = false, hovered = false, focused = false;
      let timer = null, handoff = null, current = null, incoming = null, index = 0;
      card.style.setProperty('--hyo-frame-fade', `${fadeDuration}ms`);
      const cancel = timeout => {
        if (timeout === null) return;
        clearTimeout(timeout);
        this.timers.delete(timeout);
      };
      const settle = () => {
        cancel(handoff);
        handoff = null;
        if (!incoming) return;
        if (current) current.classList.remove('is-active');
        incoming.classList.remove('is-incoming');
        incoming.classList.add('is-active');
        current = incoming;
        incoming = null;
        cover.classList.add('is-hidden');
      };
      const show = () => {
        settle();
        incoming = frames[index];
        incoming.classList.add('is-incoming');
        handoff = this.later(settle, fadeDuration + 32);
      };
      const advance = () => {
        timer = null;
        if (!wanted || this.controller.signal.aborted || document.hidden) return;
        index = (index + 1) % frames.length;
        show();
        timer = this.later(advance, interval);
      };
      const start = () => {
        wanted = true;
        if (playing || !frames.length || this.controller.signal.aborted || document.hidden) return;
        playing = true;
        index = card.dataset.randomStart === 'true' ? Math.floor(Math.random() * frames.length) : 0;
        show();
        if (frames.length > 1) timer = this.later(advance, interval);
      };
      const reset = () => {
        wanted = false;
        playing = false;
        cancel(timer);
        cancel(handoff);
        timer = handoff = null;
        cover.classList.remove('is-hidden');
        frames.forEach(image => image.classList.remove('is-active', 'is-incoming'));
        current = incoming = null;
      };
      const stop = () => {
        hovered = focused = false;
        reset();
      };
      const sync = () => hovered || focused ? start() : reset();
      card.startAnimation = start;
      card.stopAnimation = stop;
      this.listen(card, 'mouseenter', () => { hovered = true; sync(); });
      this.listen(card, 'mousemove', () => { if (!hovered) { hovered = true; sync(); } });
      this.listen(card, 'mouseleave', () => { hovered = false; sync(); });
      this.listen(card, 'focusin', () => { focused = true; sync(); });
      this.listen(card, 'focusout', event => { focused = card.contains(event.relatedTarget); sync(); });
      this.listen(card, 'transitionend', event => {
        if (event.target === incoming && event.propertyName === 'opacity') settle();
      });
      // Preserve desktop preloading; templates stay inert on touch/mobile devices.
      Promise.all(templates.map(template => new Promise(resolve => {
        const image = template.content.querySelector('img').cloneNode(true);
        image.dataset.hyoHydrated = '';
        image.loading = 'eager';
        let settled = false;
        const finish = async ok => {
          if (settled) return;
          settled = true;
          if (ok && image.decode) {
            try { await image.decode(); } catch { ok = false; }
          }
          resolve(ok ? image : null);
        };
        this.listen(image, 'load', () => finish(true), {once:true});
        this.listen(image, 'error', () => finish(false), {once:true});
        this.controller.signal.addEventListener('abort', () => finish(false), {once:true});
        template.after(image);
        if (image.complete) finish(image.naturalWidth > 0);
      }))).then(loaded => {
        if (this.controller.signal.aborted) return;
        frames = loaded.filter(Boolean);
        if (wanted) start();
      });
    }

  }

  function mount(container) {
    if (!instances.has(container)) instances.set(container, new HoyostarHoverGrid(container));
  }
  function scan(root) {
    if (root.matches?.('[data-hoyostar-grid]')) mount(root);
    root.querySelectorAll('[data-hoyostar-grid]').forEach(mount);
  }
  window.__hoyostarGridV6 = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), {once:true});
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [container, instance] of instances) if (event.target.contains(container)) instance.destroy();
  });
})();

